import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

class TpQuantityBundle extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.bindControls();

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  bindControls() {
    this.optionInputs = [...this.querySelectorAll('.tp-quantity-bundle__radio')];
    this.button = this.querySelector('[data-add-to-cart]');
    this.message = this.querySelector('[data-message]');

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', this.handleOptionChange, {
        signal: this.abortController.signal,
      });
    });

    this.button?.addEventListener('click', this.handleAddToCart, {
      signal: this.abortController.signal,
    });

    this.syncSelectedOption();
  }

  handleOptionChange = () => {
    this.syncSelectedOption();
  };

  syncSelectedOption() {
    const checkedInput = this.querySelector('.tp-quantity-bundle__radio:checked') || this.optionInputs[0];

    if (checkedInput && !checkedInput.checked) {
      checkedInput.checked = true;
    }

    this.selectedQuantity = Number(checkedInput?.value) || 1;

    this.querySelectorAll('[data-bundle-option]').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(checkedInput));
    });
  }

  handleVariantUpdate = (event) => {
    const productId = this.dataset.productId;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== productId) {
      return;
    }

    const nextBundle = event.detail.data.html.querySelector(`tp-quantity-bundle[data-block-id="${this.dataset.blockId}"]`);
    if (!nextBundle) return;

    ['data-product-id', 'data-variant-id', 'data-variant-available'].forEach((attribute) => {
      const value = nextBundle.getAttribute(attribute);
      if (value !== null) this.setAttribute(attribute, value);
    });

    const nextStyle = nextBundle.getAttribute('style');
    if (nextStyle) {
      this.setAttribute('style', nextStyle);
    }

    this.innerHTML = nextBundle.innerHTML;
    this.bindControls();
  };

  handleAddToCart = async (event) => {
    event.preventDefault();

    if (!this.button || this.button.disabled || this.dataset.variantAvailable !== 'true') return;

    this.syncSelectedOption();
    this.setMessage('');
    this.setButtonText(this.button.dataset.addingText);
    this.button.disabled = true;

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(this.buildCartPayload()),
        priority: 'high',
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchCartError(data);
        throw new Error(data.message || data.description || this.button.dataset.errorText);
      }

      this.dispatchEvent(
        new CartAddEvent(data, this.dataset.variantId, {
          source: 'product-form-component',
          itemCount: this.selectedQuantity,
          productId: this.dataset.productId,
          sections: data.sections,
          variantId: this.dataset.variantId,
        })
      );

      this.openCartDrawer();
      this.setButtonText(this.button.dataset.addedText);
      window.setTimeout(() => this.restoreButton(), 1300);
    } catch (error) {
      const fallbackMessage = this.button.dataset.errorText || 'Tente novamente';
      const message = error instanceof Error && error.message ? error.message : fallbackMessage;

      this.setMessage(message);
      this.setButtonText(fallbackMessage);
      window.setTimeout(() => this.restoreButton(), 1800);
    }
  };

  buildCartPayload() {
    const payload = {
      id: Number(this.dataset.variantId),
      quantity: this.selectedQuantity,
    };
    const sections = this.getCartSectionIds();

    if (sections) {
      payload.sections = sections;
    }

    return payload;
  }

  getCartSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    return [...sectionIds].join(',');
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');

    if (!cartDrawer) {
      const cartUrl = window.Theme?.routes?.cart_url;
      if (cartUrl) window.location.assign(cartUrl);
      return;
    }

    customElements.whenDefined('cart-drawer-component').then(() => {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    });
  }

  dispatchCartError(data) {
    this.dispatchEvent(
      new CartErrorEvent(
        this.id,
        data.message || data.description || '',
        data.description || '',
        data.errors || {}
      )
    );
  }

  setButtonText(text) {
    if (!this.button || !text) return;
    this.button.textContent = text;
  }

  restoreButton() {
    if (!this.button) return;

    this.button.disabled = this.dataset.variantAvailable !== 'true';
    this.setButtonText(this.button.dataset.defaultText);
  }

  setMessage(message) {
    if (!this.message) return;
    this.message.textContent = message;
  }
}

if (!customElements.get('tp-quantity-bundle')) {
  customElements.define('tp-quantity-bundle', TpQuantityBundle);
}
